import {useEffect, useMemo, useRef, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Select, Spin} from 'antd';
import {GET_FINANCIAL_INSTRUMENT_OPTIONS} from 'graphql/queries/assetManagement';
import {
  buildInstrumentOptionsByIdsQuery,
  hasInstrumentIdsToResolve,
  normalizeInstrumentIds,
  readInstrumentOptionsByIds,
} from 'components/financialInstrumentPicker/instrumentIds';

const SEARCH_DEBOUNCE = 300;

const label = option =>
  option?.ticker ? `${option.name} (${option.ticker})` : option?.name;

// "Companies in this story": a search by company name, limited to the
// companies listed on the story's exchange. The value is instrument ids.
const CompanySearch = ({value, onChange, exchangeId, disabled}) => {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [chosen, setChosen] = useState({});
  const timer = useRef(null);
  const ids = useMemo(() => normalizeInstrumentIds(value), [value]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(
      () => setDebounced(search.trim()),
      SEARCH_DEBOUNCE,
    );
    return () => clearTimeout(timer.current);
  }, [search]);

  const {data, loading} = useQuery(GET_FINANCIAL_INSTRUMENT_OPTIONS, {
    variables: {
      input: {
        assetClassKey: 'EQUITIES',
        instrumentKey: 'PUBLIC_EQUITY',
        explore: false,
        status: 'ENABLED',
        paginate: true,
        page: 1,
        limit: 20,
        ...(exchangeId && {exchange: exchangeId}),
        ...(debounced && {search: debounced}),
      },
    },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  // Names for saved ids that aren't in the current results.
  const results = data?.getFinancialInstruments?.data || [];
  const unknownKey = ids
    .filter(id => !chosen[id] && !results.some(option => option?.id === id))
    .join(',');
  const unknown = useMemo(
    () => (unknownKey ? unknownKey.split(',') : []),
    [unknownKey],
  );
  const byIds = useMemo(
    () => buildInstrumentOptionsByIdsQuery(unknown),
    [unknown],
  );
  const {data: resolved} = useQuery(byIds, {
    skip: !hasInstrumentIdsToResolve(unknown),
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
  });

  const options = useMemo(() => {
    const all = {};
    [
      ...Object.values(chosen),
      ...readInstrumentOptionsByIds(resolved),
      ...(data?.getFinancialInstruments?.data || []),
    ]
      .filter(option => option?.id)
      .forEach(option => {
        all[option.id] = option;
      });
    return Object.values(all);
  }, [chosen, resolved, data]);

  const handleChange = next => {
    setChosen(prev => {
      const kept = {...prev};
      options
        .filter(option => next.includes(option.id))
        .forEach(option => {
          kept[option.id] = option;
        });
      return kept;
    });
    onChange?.(next);
  };

  return (
    <Select
      mode="multiple"
      showSearch
      value={ids}
      disabled={disabled}
      loading={loading}
      onChange={handleChange}
      onSearch={setSearch}
      filterOption={false}
      placeholder="Search by company name"
      notFoundContent={
        loading ? <Spin size="small" /> : 'No matching companies'
      }
      options={options.map(option => ({
        value: option.id,
        label: label(option),
      }))}
      style={{width: '100%'}}
    />
  );
};

export default CompanySearch;
